/**
 * Talks to the self-hosted Infisical instance directly from the browser.
 *
 * The admin panel is the Infisical client here — there is no backend proxy in this path. The
 * backend's only job is `GET /secrets/infisical`, which hands back an AES-256-GCM envelope
 * containing the machine identity's `clientId`/`clientSecret` (see `decryptSecretPayload`).
 * Those credentials are exchanged for a short-lived bearer token via Universal Auth, and the
 * token is used for the v4 secrets API.
 *
 *   POST  /api/v1/auth/universal-auth/login   → { accessToken, expiresIn, tokenType }
 *   GET   /api/v4/secrets?viewSecretValue=false
 *   PATCH /api/v4/secrets/:secretName         (update)
 *   POST  /api/v4/secrets/:secretName         (create)
 *
 * Two rules this module exists to enforce:
 *
 *  1. **Values never come back.** The list call always sends `viewSecretValue=false`, and every
 *     response is put through `sanitizeSecret`, a field *allowlist*. Even if a future Infisical
 *     release starts returning `secretValue` regardless of the flag, it is dropped here rather
 *     than reaching component state or the DOM.
 *  2. **Nothing is persisted.** Credentials and access tokens live in the module-level `sessions`
 *     map for the lifetime of the page and are never written to localStorage/sessionStorage,
 *     cookies, or the console.
 *
 * SECURITY: because the panel decrypts the envelope itself, the machine identity's client secret
 * is present in the browser. Use an identity dedicated to this panel, scoped to the one
 * project/environment/path it needs, with list/create/update but *not* read-value permission.
 *
 * DEPLOYMENT: browser-to-Infisical calls are cross-origin, so the Infisical instance has to
 * allow this panel's origin (`CORS_ALLOWED_ORIGINS` / `SITE_URL` on a self-hosted deployment).
 * A request that fails with no HTTP response is almost always that, so `describeError` says so
 * instead of surfacing a bare "Network Error".
 */

import axios from "axios";
import { INFISICAL_BASE } from "../config/api";
import { getErrorMessage } from "./getErrorMessage";

/** Renew this far before the stated expiry so a call never lands on a just-expired token. */
const CLOCK_SKEW_MS = 30_000;
/** Used when Infisical omits `expiresIn`; short, since the cost of re-login is one request. */
const FALLBACK_TTL_S = 120;

/** In-memory only — deliberately not localStorage/sessionStorage. Cleared on page reload. */
const sessions = new Map();

// ─── configuration ────────────────────────────────────────────────────────────

/**
 * `{ clientId, clientSecret }` from the decrypted backend response, whatever casing it used.
 * The backend cannot select a different Infisical origin; every operation is pinned to
 * `INFISICAL_BASE`.
 */
export function toUniversalAuthCredentials(credential) {
  const clientId = credential?.client_id ?? credential?.clientId;
  const clientSecret = credential?.client_secret ?? credential?.clientSecret;
  if (typeof clientId !== "string" || !clientId.trim()) return null;
  if (typeof clientSecret !== "string" || !clientSecret.trim()) return null;

  return {
    clientId: clientId.trim(),
    clientSecret: clientSecret.trim(),
  };
}

// ─── session ──────────────────────────────────────────────────────────────────

/** Keyed by identity so two machine identities do not share a token. */
const sessionKey = ({ clientId }) => clientId;

/** Drop one session (or all of them) — e.g. after a 401 or when the screen closes. */
export function clearInfisicalSession(credentials) {
  if (!credentials) sessions.clear();
  else sessions.delete(sessionKey(credentials));
}

/**
 * Exchanges the machine identity for a short-lived bearer token.
 * The token is cached in memory with its expiry; the credentials themselves are not cached.
 */
export async function loginUniversalAuth(credentials) {
  const { clientId, clientSecret } = credentials;

  let data;
  try {
    ({ data } = await axios.post(
      `${INFISICAL_BASE}/api/v1/auth/universal-auth/login`,
      { clientId, clientSecret },
      { headers: { "Content-Type": "application/json", Accept: "application/json" } }
    ));
  } catch (err) {
    throw describeError(err, "Infisical rejected the machine identity credentials.");
  }

  const accessToken = data?.accessToken;
  if (typeof accessToken !== "string" || !accessToken) {
    throw new Error("Infisical returned no access token for these credentials.");
  }

  const ttlSeconds = Number(data?.expiresIn);
  const session = {
    accessToken,
    expiresAt:
      Date.now() +
      (Number.isFinite(ttlSeconds) && ttlSeconds > 0 ? ttlSeconds : FALLBACK_TTL_S) * 1000,
  };
  sessions.set(sessionKey({ clientId }), session);
  return session;
}

/** Cached token while it is still comfortably valid, otherwise a fresh login. */
async function getAccessToken(credentials, { forceRefresh = false } = {}) {
  const key = sessionKey(credentials);
  const cached = sessions.get(key);
  if (!forceRefresh && cached && cached.expiresAt - CLOCK_SKEW_MS > Date.now()) {
    return cached.accessToken;
  }
  sessions.delete(key);
  const { accessToken } = await loginUniversalAuth(credentials);
  return accessToken;
}

/**
 * One authenticated v4 call. A 401/403 means the token died earlier than its stated TTL (or was
 * revoked), so the identity is re-authenticated once and the call retried; a second failure is
 * a real permission problem and surfaces to the caller.
 */
async function authedRequest(credentials, config, { fallbackMessage }) {
  const send = async (forceRefresh) => {
    const token = await getAccessToken(credentials, { forceRefresh });
    return axios.request({
      ...config,
      url: `${INFISICAL_BASE}${config.url}`,
      headers: {
        ...config.headers,
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    });
  };

  try {
    return await send(false);
  } catch (err) {
    const status = err?.response?.status;
    if (status === 401 || status === 403) {
      clearInfisicalSession(credentials);
      try {
        return await send(true);
      } catch (retryErr) {
        throw describeError(retryErr, fallbackMessage);
      }
    }
    throw describeError(err, fallbackMessage);
  }
}

// ─── response sanitisation ────────────────────────────────────────────────────

/**
 * Fields the panel is allowed to know about a secret. `secretValue` is absent by design, and so
 * is `secretComment`: a comment is free text an operator could have pasted a value into, and the
 * UI has no need for it.
 */
const SECRET_FIELDS = ["id", "version", "type", "secretPath", "createdAt", "updatedAt"];

/**
 * `{ key, ...metadata }` — an allowlist copy, never the raw Infisical object. Returns null for
 * anything without a name, so a malformed row is dropped rather than rendered as `undefined`.
 */
export function sanitizeSecret(secret) {
  const key = secret?.secretKey ?? secret?.key ?? secret?.secretName;
  if (typeof key !== "string" || !key) return null;

  const safe = { key };
  for (const field of SECRET_FIELDS) {
    if (secret[field] !== undefined && secret[field] !== null) safe[field] = secret[field];
  }
  return safe;
}

/** Sorted by name so the table order doesn't shift between refreshes. */
function sanitizeSecretList(secrets) {
  return (Array.isArray(secrets) ? secrets : [])
    .map(sanitizeSecret)
    .filter(Boolean)
    .sort((a, b) => a.key.localeCompare(b.key));
}

/**
 * A network failure with no HTTP response is nearly always CORS on the Infisical side, which
 * `getErrorMessage` can only describe as "Network Error". Say what to fix instead.
 */
function describeError(err, fallback) {
  if (err instanceof Error && err.__infisical) return err;
  const panelOrigin = typeof window !== "undefined" ? window.location.origin : "the panel origin";
  const method = String(err?.config?.method ?? "request").toUpperCase();
  const wrapped = new Error(
    err?.response
      ? getErrorMessage(err, fallback)
      : `Infisical blocked the ${method} request from ${panelOrigin}. Check CORS_ALLOWED_ORIGINS and CORS_ALLOWED_METHODS on ${INFISICAL_BASE}.`
  );
  wrapped.__infisical = true;
  wrapped.status = err?.response?.status;
  return wrapped;
}

// ─── secrets API ──────────────────────────────────────────────────────────────

/**
 * Discover secret-manager scopes available to this machine identity. Infisical returns project
 * environments from the projects endpoint; secrets live at the root path unless configured
 * otherwise by the credential response.
 */
export async function listSecretScopes(credentials) {
  const res = await authedRequest(
    credentials,
    {
      method: "get",
      url: "/api/v1/projects",
      params: { type: "secret-manager" },
    },
    { fallbackMessage: "Failed to discover Infisical projects." }
  );

  const projects = Array.isArray(res.data?.projects) ? res.data.projects : [];
  return projects.flatMap((project) => {
    const projectId = String(project?.id ?? project?._id ?? "").trim();
    if (!projectId) return [];
    const projectName = String(project?.name ?? project?.slug ?? "Infisical").trim();
    const environments = Array.isArray(project?.environments) ? project.environments : [];

    return environments.flatMap((environment) => {
      const slug = String(environment?.slug ?? "").trim();
      if (!slug) return [];
      return [{
        projectId,
        environment: slug,
        secretPath: "/",
        label: `${projectName} · ${environment?.name || slug}`,
      }];
    });
  }).sort((a, b) => {
    if (a.environment === "dev" && b.environment !== "dev") return -1;
    if (b.environment === "dev" && a.environment !== "dev") return 1;
    return a.label.localeCompare(b.label);
  });
}

/** `{ projectId, environment, secretPath }`, validated and defaulted. */
function scope({ projectId, environment, secretPath }) {
  const project = String(projectId ?? "").trim();
  const env = String(environment ?? "").trim();
  if (!project) throw new Error("An Infisical project ID is required.");
  if (!env) throw new Error("An Infisical environment slug is required.");

  const path = String(secretPath ?? "").trim() || "/";
  return { projectId: project, environment: env, secretPath: path };
}

/**
 * Secret **names** for a scope. `viewSecretValue=false` asks Infisical not to send values;
 * `sanitizeSecret` guarantees it regardless of what actually comes back.
 *
 * `expandSecretReferences` is off because expanding a reference means resolving the value it
 * points at.
 */
export async function listSecretNames({ credentials, ...target }) {
  const { projectId, environment, secretPath } = scope(target);

  const res = await authedRequest(
    credentials,
    {
      method: "get",
      url: "/api/v4/secrets",
      params: {
        projectId,
        environment,
        secretPath,
        viewSecretValue: "false",
        expandSecretReferences: "false",
      },
    },
    { fallbackMessage: "Failed to list secrets from Infisical." }
  );

  return sanitizeSecretList(res.data?.secrets ?? res.data);
}

/** Writes a new value over an existing secret. Returns sanitized metadata only. */
export async function updateSecretValue({ credentials, secretName, secretValue, ...target }) {
  const { projectId, environment, secretPath } = scope(target);
  const name = String(secretName ?? "").trim();
  if (!name) throw new Error("A secret name is required.");
  if (typeof secretValue !== "string" || !secretValue) {
    throw new Error("A secret value is required.");
  }

  const res = await authedRequest(
    credentials,
    {
      method: "patch",
      url: `/api/v4/secrets/${encodeURIComponent(name)}`,
      headers: { "Content-Type": "application/json" },
      data: { projectId, environment, secretPath, secretValue },
    },
    { fallbackMessage: `Failed to update ${name} in Infisical.` }
  );

  return sanitizeSecret(res.data?.secret ?? res.data) ?? { key: name };
}

/** Permanently deletes a secret. Returns the sanitized metadata of the removed secret. */
export async function deleteSecret({ credentials, secretName, ...target }) {
  const { projectId, environment, secretPath } = scope(target);
  const name = String(secretName ?? "").trim();
  if (!name) throw new Error("A secret name is required.");

  const res = await authedRequest(
    credentials,
    {
      method: "delete",
      url: `/api/v4/secrets/${encodeURIComponent(name)}`,
      headers: { "Content-Type": "application/json" },
      data: { projectId, environment, secretPath },
    },
    { fallbackMessage: `Failed to delete ${name} in Infisical.` }
  );

  return sanitizeSecret(res.data?.secret ?? res.data) ?? { key: name };
}

/** Creates a secret that does not exist yet. Returns sanitized metadata only. */
export async function createSecret({ credentials, secretName, secretValue, ...target }) {
  const { projectId, environment, secretPath } = scope(target);
  const name = String(secretName ?? "").trim();
  if (!name) throw new Error("A secret name is required.");
  if (typeof secretValue !== "string" || !secretValue) {
    throw new Error("A secret value is required.");
  }

  const res = await authedRequest(
    credentials,
    {
      method: "post",
      url: `/api/v4/secrets/${encodeURIComponent(name)}`,
      headers: { "Content-Type": "application/json" },
      data: { projectId, environment, secretPath, secretValue, type: "shared" },
    },
    { fallbackMessage: `Failed to create ${name} in Infisical.` }
  );

  return sanitizeSecret(res.data?.secret ?? res.data) ?? { key: name };
}
